import { Fragment, useMemo, type ReactNode } from "react";
import type { User } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Must agree with `parseMentions` in core: the same leading guard, so an email
 * address in a comment is not painted as a mention here and then quietly ignored
 * by the parser that decides what becomes a request.
 */
const MENTION = /(^|[^\w@/])@([a-z][a-z0-9_-]{0,30})/gi;

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const QUOTE = /^ {0,3}>/;
const INLINE_CODE = /(`+)[^\n]*?\1/g;

/**
 * Copy of `maskQuoted` in core's `lib/mentions.ts`, and it must stay one: a
 * handle inside code or a `>` quote is talked about, not addressed, so core opens
 * no request for it — and painting it here would say otherwise. Blanks those
 * spans with spaces so indices still point into the original text.
 */
export function maskQuoted(body: string): string {
  const blank = (text: string) => text.replace(/[^\n]/g, " ");
  let fence: string | null = null;
  return body
    .split("\n")
    .map((line) => {
      if (fence !== null) {
        const close = line.match(FENCE);
        if (close && close[1]![0] === fence[0] && close[1]!.length >= fence.length && line.trim() === close[1]) fence = null;
        return blank(line);
      }
      const open = line.match(FENCE);
      if (open) {
        fence = open[1]!;
        return blank(line);
      }
      if (QUOTE.test(line)) return blank(line);
      return line.replace(INLINE_CODE, blank);
    })
    .join("\n");
}

/**
 * Lower-cased tokens that reach an agent. Core sends each agent's `aliases` —
 * built by the same function its parser uses — so this set is exactly what
 * `recordMentions` turns into a request. The display name is deliberately not
 * guessed at here: painting `@frontend` for an agent core knows as `@fe` was a
 * chip promising a request that was never opened.
 */
export function agentHandles(users: User[]): Set<string> {
  const handles = new Set<string>();
  for (const user of users) {
    if (user.kind !== "agent" || user.disabled) continue;
    if (user.aliases) {
      for (const alias of user.aliases) handles.add(alias);
      continue;
    }
    handles.add(user.id.toLowerCase());
    if (user.handle) handles.add(user.handle);
  }
  return handles;
}

/** Token → the agent's user id, for naming who a chip reaches. */
export function agentIdsByHandle(users: User[]): Map<string, string> {
  const ids = new Map<string, string>();
  for (const user of users) {
    if (user.kind !== "agent" || user.disabled) continue;
    for (const alias of user.aliases ?? [user.id.toLowerCase(), ...(user.handle ? [user.handle] : [])]) ids.set(alias, user.id);
  }
  return ids;
}

export function hasAgentMention(text: string, handles: Set<string>): boolean {
  return mentionedAgentHandles(text, handles).length > 0;
}

/** Distinct agent handles `text` addresses, lower-cased, in the order written. */
export function mentionedAgentHandles(text: string, handles: Set<string>): string[] {
  const out: string[] = [];
  for (const match of maskQuoted(text).matchAll(MENTION)) {
    const handle = (match[2] ?? "").toLowerCase();
    if (handles.has(handle) && !out.includes(handle)) out.push(handle);
  }
  return out;
}

/**
 * Splits a run of text into plain and mention parts.
 *
 * Exported because the markdown renderer needs the same split inside every text
 * node it emits, and the one thing that must not happen is a second copy of the
 * regex above drifting from core's: a mention the UI paints and the parser then
 * ignores is a request the user believes they made and nobody received.
 */
export function splitMentions(text: string, handles: Set<string>): Array<{ text: string; mention: boolean }> {
  const out: Array<{ text: string; mention: boolean }> = [];
  let cursor = 0;
  for (const match of maskQuoted(text).matchAll(MENTION)) {
    const lead = match[1] ?? "";
    const handle = match[2] ?? "";
    if (!handles.has(handle.toLowerCase())) continue;
    const at = (match.index ?? 0) + lead.length;
    if (at > cursor) out.push({ text: text.slice(cursor, at), mention: false });
    out.push({ text: `@${handle}`, mention: true });
    cursor = at + handle.length + 1;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), mention: false });
  return out;
}

/** The highlight itself, so the markdown renderer paints mentions identically. */
export function MentionChip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded bg-primary/15 px-1 py-px font-medium text-primary ring-1 ring-inset ring-primary/25">
      {children}
    </span>
  );
}

/** Mention-highlighted nodes for one run of plain text. */
export function mentionNodes(text: string, handles: Set<string>, keyPrefix = "m"): ReactNode[] {
  return splitMentions(text, handles).map((part, index) =>
    part.mention ? (
      <MentionChip key={`${keyPrefix}-${index}`}>{part.text}</MentionChip>
    ) : (
      <Fragment key={`${keyPrefix}-${index}`}>{part.text}</Fragment>
    ),
  );
}

/**
 * Comment body with agent mentions picked out. A mention is the difference
 * between a note and a request, so it has to look different from the prose
 * around it — otherwise the user cannot tell which of their comments actually
 * asked Claude for something.
 *
 * Plain text only. A comment Claude wrote is markdown and goes through
 * `<Markdown>` instead, which reuses the same highlighting inside its text nodes.
 */
export function MentionText({ text, handles, className }: { text: string; handles: Set<string>; className?: string }) {
  const nodes = useMemo(() => mentionNodes(text, handles), [text, handles]);
  return <span className={cn("whitespace-pre-wrap break-words", className)}>{nodes}</span>;
}
